import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { socialAPI } from '../../utils/api';

// Async thunk для подписки на пользователя
export const followUser = createAsyncThunk(
  'social/followUser',
  async (userId, { rejectWithValue }) => {
    try {
      await socialAPI.followUser(userId);
      return userId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to follow user');
    }
  }
);

// Async thunk для отписки от пользователя
export const unfollowUser = createAsyncThunk(
  'social/unfollowUser',
  async (userId, { rejectWithValue }) => {
    try {
      await socialAPI.unfollowUser(userId);
      return userId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to unfollow user');
    }
  }
);

// Дедупликация: один запрос follow/status на userId, остальные ждут тот же результат (много карточек = один запрос на автора).
const followStatusPromises = new Map();

// Async thunk для проверки статуса подписки
export const checkFollowStatus = createAsyncThunk(
  'social/checkFollowStatus',
  async (userId, { rejectWithValue, getState }) => {
    const state = getState();
    if (state.social?.followStatus && Object.prototype.hasOwnProperty.call(state.social.followStatus, userId)) {
      return { userId, isFollowing: state.social.followStatus[userId] };
    }
    let promise = followStatusPromises.get(userId);
    if (!promise) {
      promise = (async () => {
        try {
          const response = await socialAPI.checkFollowStatus(userId);
          return { userId, isFollowing: response.data?.is_following ?? false };
        } finally {
          followStatusPromises.delete(userId);
        }
      })();
      followStatusPromises.set(userId, promise);
    }
    try {
      return await promise;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to check follow status');
    }
  }
);

// Async thunk для получения подписчиков
export const fetchFollowers = createAsyncThunk(
  'social/fetchFollowers',
  async ({ userId, limit = 20, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getFollowers(userId, { limit, offset });
      return { userId, followers: response.data, hasMore: response.data.length === limit };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch followers');
    }
  }
);

// Async thunk для получения подписок
export const fetchFollowing = createAsyncThunk(
  'social/fetchFollowing',
  async ({ userId, limit = 20, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getFollowing(userId, { limit, offset });
      return { userId, following: response.data, hasMore: response.data.length === limit };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch following');
    }
  }
);

// Async thunk для создания комментария
export const createComment = createAsyncThunk(
  'social/createComment',
  async (commentData, { rejectWithValue }) => {
    try {
      const response = await socialAPI.createComment(commentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create comment');
    }
  }
);

// Async thunk для получения комментариев
export const fetchComments = createAsyncThunk(
  'social/fetchComments',
  async ({ contentType, contentId, limit = 20, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getComments(contentType, contentId, { limit, offset });
      return { 
        contentType, 
        contentId, 
        comments: response.data, 
        hasMore: response.data.length === limit 
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch comments');
    }
  }
);

// Async thunk для редактирования комментария
export const editComment = createAsyncThunk(
  'social/editComment',
  async ({ commentId, text }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.updateComment(commentId, { text });
      return { commentId, updates: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to edit comment');
    }
  }
);

// Async thunk для удаления комментария
export const deleteComment = createAsyncThunk(
  'social/deleteComment',
  async (commentId, { rejectWithValue }) => {
    try {
      await socialAPI.deleteComment(commentId);
      return commentId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete comment');
    }
  }
);

const socialSlice = createSlice({
  name: 'social',
  initialState: {
    // Подписки
    followers: {}, // { userId: { users: [], hasMore: boolean, isLoading: boolean } }
    following: {}, // { userId: { users: [], hasMore: boolean, isLoading: boolean } }
    followStatus: {}, // { userId: boolean } - статус подписки текущего пользователя
    
    // Комментарии
    comments: {}, // { 'contentType_contentId': { comments: [], hasMore: boolean, isLoading: boolean } }
    
    // Общее состояние
    isLoading: false,
    error: null,
  },
  reducers: {
    // Обновление статуса подписки (приходит через WebSocket)
    updateFollowStatus: (state, action) => {
      const { userId, isFollowing } = action.payload;
      state.followStatus[userId] = isFollowing;
    },

    // Добавление нового комментария (приходит через WebSocket)
    addComment: (state, action) => {
      const comment = action.payload;
      const key = `${comment.contentType}_${comment.contentId}`;
      
      if (!state.comments[key]) {
        state.comments[key] = { comments: [], hasMore: true, isLoading: false };
      }
      
      // Проверяем, нет ли уже такого комментария
      const exists = state.comments[key].comments.find(c => c.id === comment.id);
      if (!exists) {
        state.comments[key].comments.unshift(comment);
      }
    },

    // Обновление комментария
    updateComment: (state, action) => {
      const { id, updates } = action.payload;
      
      // Ищем комментарий во всех списках
      Object.keys(state.comments).forEach(key => {
        const commentIndex = state.comments[key].comments.findIndex(c => c.id === id);
        if (commentIndex !== -1) {
          state.comments[key].comments[commentIndex] = {
            ...state.comments[key].comments[commentIndex],
            ...updates
          };
        }
      });
    },

    // Удаление комментария
    removeComment: (state, action) => {
      const commentId = action.payload;
      
      Object.keys(state.comments).forEach(key => {
        state.comments[key].comments = state.comments[key].comments.filter(
          c => c.id !== commentId
        );
      });
    },

    // Очистка ошибки
    clearError: (state) => {
      state.error = null;
    },

    // Сброс состояния
    resetSocialState: (state) => {
      state.followers = {};
      state.following = {};
      state.followStatus = {};
      state.comments = {};
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Подписка на пользователя
      .addCase(followUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(followUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const userId = action.payload;
        state.followStatus[userId] = true;
      })
      .addCase(followUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Отписка от пользователя
      .addCase(unfollowUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(unfollowUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const userId = action.payload;
        state.followStatus[userId] = false;
      })
      .addCase(unfollowUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Проверка статуса подписки
      .addCase(checkFollowStatus.fulfilled, (state, action) => {
        const { userId, isFollowing } = action.payload;
        state.followStatus[userId] = isFollowing;
      })
      .addCase(checkFollowStatus.rejected, (state, action) => {
        console.error('❌ checkFollowStatus rejected:', action.payload);
        state.error = action.payload;
      })
      
      // Получение подписчиков
      .addCase(fetchFollowers.pending, (state, action) => {
        const { userId } = action.meta.arg;
        if (!state.followers[userId]) {
          state.followers[userId] = { users: [], hasMore: true, isLoading: false };
        }
        state.followers[userId].isLoading = true;
      })
      .addCase(fetchFollowers.fulfilled, (state, action) => {
        const { userId, followers, hasMore } = action.payload;
        state.followers[userId] = { users: followers, hasMore, isLoading: false };
      })
      .addCase(fetchFollowers.rejected, (state, action) => {
        const { userId } = action.meta.arg;
        if (state.followers[userId]) {
          state.followers[userId].isLoading = false;
        }
        state.error = action.payload;
      })
      
      // Получение подписок
      .addCase(fetchFollowing.pending, (state, action) => {
        const { userId } = action.meta.arg;
        if (!state.following[userId]) {
          state.following[userId] = { users: [], hasMore: true, isLoading: false };
        }
        state.following[userId].isLoading = true;
      })
      .addCase(fetchFollowing.fulfilled, (state, action) => {
        const { userId, following, hasMore } = action.payload;
        state.following[userId] = { users: following, hasMore, isLoading: false };
      })
      .addCase(fetchFollowing.rejected, (state, action) => {
        const { userId } = action.meta.arg;
        if (state.following[userId]) {
          state.following[userId].isLoading = false;
        }
        state.error = action.payload;
      })
      
      // Создание комментария
      .addCase(createComment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createComment.fulfilled, (state, action) => {
        state.isLoading = false;
        const comment = action.payload;
        const key = `${comment.contentType}_${comment.contentId}`;
        
        if (!state.comments[key]) {
          state.comments[key] = { comments: [], hasMore: true, isLoading: false };
        }
        
        state.comments[key].comments.unshift(comment);
      })
      .addCase(createComment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Получение комментариев
      .addCase(fetchComments.pending, (state, action) => {
        const { contentType, contentId } = action.meta.arg;
        const key = `${contentType}_${contentId}`;
        
        if (!state.comments[key]) {
          state.comments[key] = { comments: [], hasMore: true, isLoading: false };
        }
        state.comments[key].isLoading = true;
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        const { contentType, contentId, comments, hasMore } = action.payload;
        const key = `${contentType}_${contentId}`;
        
        state.comments[key] = { comments, hasMore, isLoading: false };
      })
      .addCase(fetchComments.rejected, (state, action) => {
        const { contentType, contentId } = action.meta.arg;
        const key = `${contentType}_${contentId}`;
        
        if (state.comments[key]) {
          state.comments[key].isLoading = false;
        }
        state.error = action.payload;
      })
      
      // Редактирование комментария
      .addCase(editComment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(editComment.fulfilled, (state, action) => {
        state.isLoading = false;
        const { commentId, updates } = action.payload;
        
        // Ищем комментарий во всех списках и обновляем
        Object.keys(state.comments).forEach(key => {
          const commentIndex = state.comments[key].comments.findIndex(c => c.id === commentId);
          if (commentIndex !== -1) {
            state.comments[key].comments[commentIndex] = {
              ...state.comments[key].comments[commentIndex],
              ...updates,
              isEdited: true,
              updatedAt: new Date().toISOString()
            };
          }
        });
      })
      .addCase(editComment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Удаление комментария
      .addCase(deleteComment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.isLoading = false;
        const commentId = action.payload;
        
        // Удаляем комментарий из всех списков
        Object.keys(state.comments).forEach(key => {
          state.comments[key].comments = state.comments[key].comments.filter(
            c => c.id !== commentId
          );
        });
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Экспортируем actions
export const {
  updateFollowStatus,
  addComment,
  updateComment,
  removeComment,
  clearError,
  resetSocialState,
} = socialSlice.actions;

// Селекторы
export const selectFollowers = (state, userId) => state.social.followers[userId] || { users: [], hasMore: true, isLoading: false };
export const selectFollowing = (state, userId) => state.social.following[userId] || { users: [], hasMore: true, isLoading: false };
export const selectFollowStatus = (state, userId) => state.social.followStatus[userId] || false;
export const selectComments = (state, contentType, contentId) => {
  const key = `${contentType}_${contentId}`;
  return state.social.comments[key] || { comments: [], hasMore: true, isLoading: false };
};
export const selectSocialLoading = (state) => state.social.isLoading;
export const selectSocialError = (state) => state.social.error;

export default socialSlice.reducer;
